import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Button } from 'react-bootstrap';
import eventLogService from '../../services/eventLogService';
import { US_STATES, TITLES } from '../../utils/constants';
import './PatientsLogModal.css';

const PATIENT_INFO_LOG_PLACEHOLDER_KEYS = [
  'Patient name',
  'Date Of Birth',
  'Phone Number',
  'Email',
  'Address',
  'City',
  'Rpm Start Date',
  'Agreement',
  'Status',
  'Height / Height Unit',
  'Weight / Weight Unit'
];

function parseJsonObject(raw) {
  if (raw == null || raw === '') return null;
  if (typeof raw === 'object' && !Array.isArray(raw)) return raw;
  try {
    const p = JSON.parse(raw);
    if (p && typeof p === 'object' && !Array.isArray(p)) return p;
    return { Value: JSON.stringify(p) };
  } catch {
    return { Value: String(raw) };
  }
}

export function formatActionLabel(action) {
  if (!action) return '—';
  return String(action)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export function formatLogDate(dateString) {
  if (!dateString) return 'N/A';
  const d = new Date(dateString);
  if (Number.isNaN(d.getTime())) return String(dateString);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const yy = String(d.getFullYear()).slice(-2);
  let hours = d.getHours();
  const am = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12;
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${mm}-${dd}-${yy} ${hours}:${mi} ${am}`;
}

export function moduleDisplayName(moduleId) {
  const map = {
    Patient: 'Patient',
    'Patient Schedule': 'Schedule & targets',
    'Patient Meter': 'Meter mapping',
    'Patient Document': 'Uploaded documents'
  };
  return map[moduleId] || moduleId || 'Patient';
}

function unionKeys(oldObj, newObj) {
  const a = oldObj && typeof oldObj === 'object' ? oldObj : {};
  const b = newObj && typeof newObj === 'object' ? newObj : {};
  return [...new Set([...Object.keys(a), ...Object.keys(b)])].sort((x, y) => x.localeCompare(y));
}

function normVal(v) {
  if (v == null || v === '') return '';
  if (typeof v === 'object') {
    try {
      return JSON.stringify(v);
    } catch {
      return String(v);
    }
  }
  return String(v);
}

function flattenLogData(obj) {
  if (!obj || typeof obj !== 'object') return obj;
  const out = {};
  
  Object.keys(obj).forEach(key => {
    const val = obj[key];
    
    // Flatten dailySchedule/monthlySchedule arrays
    if (Array.isArray(val) && (key === 'dailySchedule' || key === 'monthlySchedule')) {
      val.forEach(item => {
        if (item && item.name) {
          out[`${key}: ${item.name}`] = item.time || item.value || '—';
        }
      });
      return;
    }
    
    // Flatten targets array
    if (Array.isArray(val) && key === 'targets') {
      val.forEach(item => {
        if (item && item.name) {
          const range = item.min != null && item.max != null ? `${item.min} - ${item.max}` : (item.value || '—');
          out[`Target: ${item.name}`] = range;
        }
      });
      return;
    }

    // Flatten patient schedule arrays so changed fields appear clearly.
    if (
      Array.isArray(val) &&
      (
        key === 'bloodPressureTimePeriod' ||
        key === 'bloodGlucoseTimePeriod' ||
        key === 'weightTimePeriod'
      )
    ) {
      val.forEach((item, idx) => {
        if (!item || typeof item !== 'object') return;
        const periodName = item.name || `Period ${idx + 1}`;
        const start = item.startTime || item.start || '';
        const end = item.endTime || item.end || '';
        out[`${key}: ${periodName} (start)`] = start || '—';
        out[`${key}: ${periodName} (end)`] = end || '—';
      });
      return;
    }

    if (
      Array.isArray(val) &&
      (
        key === 'bloodPressureTargetRange' ||
        key === 'bloodGlucoseTargetRange'
      )
    ) {
      val.forEach((item, idx) => {
        if (!item || typeof item !== 'object') return;
        const targetName = item.name || `Target ${idx + 1}`;
        if (key === 'bloodPressureTargetRange') {
          const sysMin = item.systolicMin ?? item.sysMin ?? '';
          const sysMax = item.systolicMax ?? item.sysMax ?? '';
          const diaMin = item.diastolicMin ?? item.diaMin ?? '';
          const diaMax = item.diastolicMax ?? item.diaMax ?? '';
          out[`${key}: ${targetName} (systolic)`] = `${sysMin} - ${sysMax}`.trim();
          out[`${key}: ${targetName} (diastolic)`] = `${diaMin} - ${diaMax}`.trim();
        } else {
          const min = item.minimum ?? item.min ?? '';
          const max = item.maximum ?? item.max ?? '';
          out[`${key}: ${targetName}`] = `${min} - ${max}`.trim();
        }
      });
      return;
    }

    // Flatten meter_mapping arrays
    if (Array.isArray(val) && (key === 'meters' || key === 'meter_mappings' || Array.isArray(val[0]) || (val[0] && val[0].serial_number))) {
      val.forEach((item, idx) => {
        if (item && typeof item === 'object') {
           const model = item.model_number || item.modelNumber || 'Unknown Model';
           const sn = item.serial_number || item.serialNumber || 'N/A';
           const gateway = item.gateway_serial_number || item.gatewaySerialNumber || '';
           const ext = item.extension || '';
           out[`Meter ${idx + 1}: Model`] = model;
           out[`Meter ${idx + 1}: Serial`] = sn;
           if (ext) out[`Meter ${idx + 1}: Extension`] = ext;
           if (gateway) out[`Meter ${idx + 1}: Gateway`] = gateway;
        }
      });
      return;
    }

    if (typeof val === 'object' && val !== null && !Array.isArray(val)) {
        Object.keys(val).forEach(subKey => {
            out[`${key}: ${subKey}`] = val[subKey];
        });
    } else {
        out[key] = val;
    }
  });
  
  return out;
}

export function ComparisonTables({ oldData, newData, placeholderFieldKeys = null }) {
  const oldRaw = parseJsonObject(oldData);
  const newRaw = parseJsonObject(newData);
  
  const oldObj = flattenLogData(oldRaw);
  const newObj = flattenLogData(newRaw);
  
  let keys = unionKeys(oldObj, newObj);

  // Only show fields that actually changed
  keys = keys.filter((key) => {
    const oVal = oldObj && Object.prototype.hasOwnProperty.call(oldObj, key) ? normVal(oldObj[key]) : '';
    const nVal = newObj && Object.prototype.hasOwnProperty.call(newObj, key) ? normVal(newObj[key]) : '';
    return oVal !== nVal;
  });

  if (keys.length === 0 && Array.isArray(placeholderFieldKeys) && placeholderFieldKeys.length > 0) {
    keys = [...placeholderFieldKeys];
  }

  if (keys.length === 0) {
    return (
      <div className="patients-log-compare">
        <div className="patients-log-table-wrap">
          <p className="patients-log-table-title">Old Data</p>
          <table className="patients-log-table">
            <tbody>
              <tr>
                <th scope="row"> </th>
                <td><span className="patients-log-empty-cell">—</span></td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="patients-log-table-wrap">
          <p className="patients-log-table-title">New Data</p>
          <table className="patients-log-table">
            <tbody>
              <tr>
                <th scope="row"> </th>
                <td><span className="patients-log-empty-cell">—</span></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  const renderRows = (side) =>
    keys.map((key) => {
      const oVal = oldObj && Object.prototype.hasOwnProperty.call(oldObj, key) ? oldObj[key] : null;
      const nVal = newObj && Object.prototype.hasOwnProperty.call(newObj, key) ? newObj[key] : null;
      
      const val = side === 'old' ? oVal : nVal;
      let display = normVal(val);
      
      // Map IDs to names for specific keys
      const stateKeys = ['state', 'stateId', 'state_id', 'address_state_id'];
      if (val != null) {
        if (stateKeys.includes(key)) {
          const state = US_STATES.find(s => String(s.id) === String(val) || String(s.code) === String(val));
          if (state) display = state.name;
        } else if (key === 'title_id') {
          const title = TITLES.find(t => String(t.id) === String(val));
          if (title) display = title.label;
        }
      }

      const otherVal = side === 'old' ? nVal : oVal;
      let other = normVal(otherVal);
      if (otherVal != null) {
        if (stateKeys.includes(key)) {
          const state = US_STATES.find(s => String(s.id) === String(otherVal) || String(s.code) === String(otherVal));
          if (state) other = state.name;
        } else if (key === 'title_id') {
          const title = TITLES.find(t => String(t.id) === String(otherVal));
          if (title) other = title.label;
        }
      }

      const changed = display !== other;
      return (
        <tr key={`${side}-${key}`} className={changed ? 'patients-log-row-changed' : ''}>
          <th scope="row">{key}</th>
          <td className={side === 'new' ? 'patients-log-new-data-text' : ''}>
            {display ? (
              display
            ) : (
              <span className="patients-log-empty-cell">—</span>
            )}
          </td>
        </tr>
      );
    });

  return (
    <div className="patients-log-compare">
      <div className="patients-log-table-wrap">
        <p className="patients-log-table-title">Old Data</p>
        <table className="patients-log-table">
          <tbody>{renderRows('old')}</tbody>
        </table>
      </div>
      <div className="patients-log-table-wrap">
        <p className="patients-log-table-title">New Data</p>
        <table className="patients-log-table">
          <tbody>{renderRows('new')}</tbody>
        </table>
      </div>
    </div>
  );
}

export const PatientsLogContent = ({ show = true, moduleId, patientId, practiceId, section = null }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalLogs, setTotalLogs] = useState(0);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (show) setPage(1);
  }, [show, moduleId, patientId, practiceId]);

  const fetchLogs = useCallback(async () => {
    if (!moduleId || patientId == null || patientId === '') return;

    setLoading(true);
    setError(null);
    try {
      const pidNum = Number(patientId);
      const patientIdParam = Number.isFinite(pidNum) ? pidNum : patientId;
      const pracNum = practiceId != null && practiceId !== '' ? Number(practiceId) : null;
      const practiceIdParam =
        pracNum != null && Number.isFinite(pracNum) ? pracNum : practiceId || undefined;

      const response = await eventLogService.getLogs(moduleId, null, {
        page,
        limit: 10,
        ...(practiceIdParam != null && practiceIdParam !== '' && { practiceId: practiceIdParam }),
        patientId: patientIdParam,
        ...(section ? { section } : {})
      });

      if (response && response.success) {
        console.log(`[PatientsLogModal] Fetched logs for moduleId: ${moduleId}`, response.logs);
        setLogs(response.logs ?? []);
        setTotalPages(Math.max(1, Number(response.pagination?.total_pages) || 1));
        setTotalLogs(response.pagination?.total ?? 0);
      } else {
        setLogs([]);
        setTotalPages(1);
        setTotalLogs(0);
      }
    } catch (err) {
      console.error('Failed to fetch patients log:', err);
      setError('Unable to load event logs.');
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [moduleId, practiceId, patientId, page, section]);

  useEffect(() => {
    if (!show) return;
    fetchLogs();
  }, [show, fetchLogs]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) setPage(newPage);
  };

  const startIndex = (page - 1) * 10;

  return (
    <>
      {loading ? (
          <div className="text-center py-4 text-muted">Loading logs…</div>
        ) : error ? (
          <div className="text-center py-4 text-danger">{error}</div>
        ) : logs.length === 0 ? (
            <div className="patients-log-entry">
              <div className="patients-log-entry-header">
                <div className="patients-log-entry-badge">1</div>
                <div className="patients-log-meta-grid">
                  <div className="patients-log-meta-row">
                    <span className="patients-log-meta-label">User Name:</span>
                    <span className="patients-log-meta-value">—</span>
                  </div>
                  <div className="patients-log-meta-row">
                    <span className="patients-log-meta-label">Action:</span>
                    <span className="patients-log-meta-value">—</span>
                  </div>
                  <div className="patients-log-meta-row">
                    <span className="patients-log-meta-label">Module:</span>
                    <span className="patients-log-meta-value">{moduleDisplayName(moduleId)}</span>
                  </div>
                  <div className="patients-log-meta-row">
                    <span className="patients-log-meta-label">Date:</span>
                    <span className="patients-log-meta-value">—</span>
                  </div>
                </div>
              </div>
              <ComparisonTables
                oldData={null}
                newData={null}
                placeholderFieldKeys={moduleId === 'Patient' ? PATIENT_INFO_LOG_PLACEHOLDER_KEYS : null}
              />
            </div>
        ) : (
          <>
            {['Patient', 'Patient Schedule', 'Patient Meter'].includes(moduleId) ? (
              logs.map((log, i) => (
                <div key={log.id ?? i} className="patients-log-entry">
                  <div className="patients-log-entry-header">
                    <div className="patients-log-entry-badge">{startIndex + i + 1}</div>
                    <div className="patients-log-meta-grid">
                      <div className="patients-log-meta-row">
                        <span className="patients-log-meta-label">User Name:</span>
                        <span className="patients-log-meta-value">{log.performer_name?.trim() || 'System'}</span>
                      </div>
                      <div className="patients-log-meta-row">
                        <span className="patients-log-meta-label">Action:</span>
                        <span className="patients-log-meta-value">{formatActionLabel(log.action)}</span>
                      </div>
                      <div className="patients-log-meta-row">
                        <span className="patients-log-meta-label">Module:</span>
                        <span className="patients-log-meta-value">{moduleDisplayName(log.module || moduleId)}</span>
                      </div>
                      <div className="patients-log-meta-row">
                        <span className="patients-log-meta-label">Date:</span>
                        <span className="patients-log-meta-value">{formatLogDate(log.created_at)}</span>
                      </div>
                    </div>
                  </div>
                  <ComparisonTables oldData={log.data} newData={log.update_data} />
                </div>
              ))
            ) : (
              <div className="table-responsive">
                <table className="patients-log-table patients-log-table-compact">
                  <thead className="patients-log-thead-premium">
                    <tr>
                      <th style={{ width: '50px' }}>#</th>
                      <th>User</th>
                      <th>Practice</th>
                      <th>Module</th>
                      <th>Action</th>
                      <th>Date</th>
                      <th>Data</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map((log, i) => {
                      let dataStr = 'N/A';
                      const mergedData = { ...(parseJsonObject(log.data) || {}), ...(parseJsonObject(log.update_data) || {}) };
                      const keys = Object.keys(mergedData);
                      if (keys.length > 0) {
                        dataStr = keys.map(k => {
                            const val = typeof mergedData[k] === 'object' ? JSON.stringify(mergedData[k]) : mergedData[k];
                            return `${k}: ${val}`;
                        }).join('\n');
                      }
                      return (
                        <tr key={log.id ?? i}>
                          <td>{startIndex + i + 1}</td>
                          <td className="fw-semibold text-primary">{log.performer_name?.trim() || 'System'}</td>
                          <td>{log.practice_name || 'N/A'}</td>
                          <td><span className="badge bg-light text-dark border">{moduleDisplayName(log.module || moduleId)}</span></td>
                          <td>{formatActionLabel(log.action)}</td>
                          <td className="text-muted">{formatLogDate(log.created_at)}</td>
                          <td style={{ whiteSpace: 'pre-wrap', fontSize: '11px' }}>
                            <div className="patients-log-data-cell">
                                {dataStr}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {totalPages > 1 && (
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 patients-log-pagination">
                <div className="small text-muted">Total entries: {totalLogs}</div>
                <div className="d-flex align-items-center gap-2">
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    disabled={page === 1}
                    onClick={() => handlePageChange(page - 1)}
                  >
                    Previous
                  </Button>
                  <span className="small text-muted">
                    Page {page} of {totalPages}
                  </span>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    disabled={page === totalPages}
                    onClick={() => handlePageChange(page + 1)}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </>
  );
};

const PatientsLogModal = ({ show, onHide, moduleId, patientId, practiceId, section = null, withSidebarOffset = false }) => {
  return (
    <Modal
      show={show}
      onHide={onHide}
      size="xl"
      className={`patients-log-modal${withSidebarOffset ? ' patients-log-modal--with-sidebar' : ''}`}
      centered={!withSidebarOffset}
    >
      <Modal.Header closeButton>
        <Modal.Title>Patients Log</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <PatientsLogContent
          show={show}
          moduleId={moduleId}
          patientId={patientId}
          practiceId={practiceId}
          section={section}
        />
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default PatientsLogModal;
